import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  AdjustProductInstanceSpecificationsOptions,
  AdjustProductInstanceSpecificationsResponse,
  AssignProductInstanceToSiteIdentifiers,
  AssignProductInstanceToSiteOptions,
  AssignProductInstanceToSiteResponse,
  CancelPackageOptions,
  CancelPackageResponse,
  CancelProductInstanceOptions,
  CancelProductInstanceResponse,
  CreatePackageOptions,
  CreatePackageResponse,
  CreatePackageV2Options,
  Package,
  PackageQuery,
  PackagesQueryBuilder,
  QueryPackagesResponse,
  UnassignProductInstanceFromSiteOptions,
  UnassignProductInstanceFromSiteResponse,
  UpdatePackageExternalIdIdentifiers,
  UpdatePackageExternalIdResponse,
  adjustProductInstanceSpecifications as universalAdjustProductInstanceSpecifications,
  assignProductInstanceToSite as universalAssignProductInstanceToSite,
  cancelPackage as universalCancelPackage,
  cancelProductInstance as universalCancelProductInstance,
  createPackage as universalCreatePackage,
  createPackageV2 as universalCreatePackageV2,
  getPackage as universalGetPackage,
  queryPackages as universalQueryPackages,
  typedQueryPackages as universalTypedQueryPackages,
  unassignProductInstanceFromSite as universalUnassignProductInstanceFromSite,
  updatePackageExternalId as universalUpdatePackageExternalId,
} from './premium-reseller-v1-packages-packages.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/packages' };

export function createPackage(httpClient: HttpClient): CreatePackageSignature {
  return (options?: NonNullablePaths<CreatePackageOptions, `products`, 2>) =>
    universalCreatePackage(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CreatePackageSignature {
  /**
   * Creates a new package.
   *
   * Deprecated: This method is deprecated and will be removed by 2024-12-31.
   * Use com.wixpress.premium.reseller.packages.v1.Packages.CreatePackageV2 instead.
   *
   * HTTP Request:
   * POST /v1/packages
   *
   * Permissions:
   * Requires the "premium.reseller-package.manage" permission with MANUAL type.
   *
   * Event:
   * A PRIVATE callback will be triggered when the package is successfully created.
   *
   * Required Fields:
   * - CreatePackageRequest.products
   *
   * Maturity: GA
   * Exposure: PUBLIC
   * @deprecated
   */
  (options?: NonNullablePaths<CreatePackageOptions, `products`, 2>): Promise<
    NonNullablePaths<
      CreatePackageResponse,
      | `package._id`
      | `package.accountId`
      | `package.productInstances`
      | `package.productInstances.${number}.billingInfo.type`
      | `package.productInstances.${number}.billingInfo.cycleDuration.unit`
      | `package.productInstances.${number}.billingInfo.cycleDuration.count`
      | `package.productInstances.${number}.instanceId`
      | `package.productInstances.${number}.catalogProductId`
      | `package.productInstances.${number}.status`
      | `package.productInstances.${number}.failure.code`
      | `package.productInstances.${number}.failure.message`,
      7
    >
  >;
}

export function createPackageV2(
  httpClient: HttpClient
): CreatePackageV2Signature {
  return (
    options?: NonNullablePaths<
      CreatePackageV2Options,
      `products` | `products.${number}.countryCode`,
      4
    >
  ) =>
    universalCreatePackageV2(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CreatePackageV2Signature {
  /**
   * Creates a package of product instances.
   *
   *
   * You must pass the relevant Wix account ID in the header of the call. In
   * the DIY flow, we recommend to pass the customer's sub-account ID instead
   * of your main reseller account ID.
   *
   * You may also pass a Wix site ID for each product in the body of the call.
   * If you omit the site ID, a floating product instance is created.
   *
   * When Wix customers purchase a specific paid service or product, Wix may offer
   * them time-limited free access to a different product. For example, customers
   * get a voucher for a free 1-year domain registration when purchasing any Wix
   * Premium plan. If you want to offer your customers the same benefit, create a
   * package containing the original product first. Then, create a second package
   * with the additional product. In the second Create Package call, pass the
   * instance ID of the original product as `referenceProductInstanceId`. This way,
   * Wix doesn't charge you for the additional product. Make sure that the status
   * of the referenced product is either `"PENDING"` or `"ACTIVE"`. Note that you
   * can use each product instance only a single time as reference instance.
   *
   * You need to pass a `countryCode` to specify where customers can claim
   * vouchers that may come with a product. Contact the
   * [Wix B2B sales team](mailto:bizdev@wix.com) to get information about
   * vouchers and supported locations.
   *
   * > **Important**: This call requires an account level API key and cannot be
   * > authenticated with the standard authorization header.
   */
  (
    options?: NonNullablePaths<
      CreatePackageV2Options,
      `products` | `products.${number}.countryCode`,
      4
    >
  ): Promise<
    NonNullablePaths<
      CreatePackageResponse,
      | `package._id`
      | `package.accountId`
      | `package.productInstances`
      | `package.productInstances.${number}.billingInfo.type`
      | `package.productInstances.${number}.billingInfo.cycleDuration.unit`
      | `package.productInstances.${number}.billingInfo.cycleDuration.count`
      | `package.productInstances.${number}.instanceId`
      | `package.productInstances.${number}.catalogProductId`
      | `package.productInstances.${number}.status`
      | `package.productInstances.${number}.failure.code`
      | `package.productInstances.${number}.failure.message`,
      7
    >
  >;
}

export function cancelPackage(httpClient: HttpClient): CancelPackageSignature {
  return (_id: string, options?: CancelPackageOptions) =>
    universalCancelPackage(
      _id,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CancelPackageSignature {
  /**
   * Cancels all product instances included in the package and the customer
   * immediately loses access to the canceled functionality.
   *
   *
   * You must pass the ID of the Wix account that the package belongs to in the
   * header of the call. The call fails, if the package and Wix account don't
   * match.
   *
   * If a canceled product instance is a requirement for another Wix service, that
   * functionality is also no longer available to the customer. For example, if
   * you cancel a Premium plan, a previously connected domain is automatically
   * disconnected from the site.
   *
   * > **Important**: This call requires an account level API key and cannot be
   * > authenticated with the standard authorization header.
   * @param - ID of the package to cancel.
   */
  (_id: string, options?: CancelPackageOptions): Promise<
    NonNullablePaths<
      CancelPackageResponse,
      | `package._id`
      | `package.accountId`
      | `package.productInstances`
      | `package.productInstances.${number}.billingInfo.type`
      | `package.productInstances.${number}.billingInfo.cycleDuration.unit`
      | `package.productInstances.${number}.billingInfo.cycleDuration.count`
      | `package.productInstances.${number}.instanceId`
      | `package.productInstances.${number}.catalogProductId`
      | `package.productInstances.${number}.status`
      | `package.productInstances.${number}.failure.code`
      | `package.productInstances.${number}.failure.message`,
      7
    >
  >;
}

export function getPackage(httpClient: HttpClient): GetPackageSignature {
  return (_id: string) =>
    universalGetPackage(
      _id,
      // @ts-ignore
      { httpClient }
    );
}

interface GetPackageSignature {
  /**
   * Retrieves a package.
   *
   *
   * You must pass the ID of the Wix account that the package belongs to in the
   * header of the call. The call fails, if the package and Wix account don't
   * match.
   *
   * > **Important**: This call requires an account level API key and cannot be
   * > authenticated with the standard authorization header.
   * @param - Package ID.
   * @returns Retrieved package.
   */
  (_id: string): Promise<
    NonNullablePaths<
      Package,
      | `_id`
      | `accountId`
      | `productInstances`
      | `productInstances.${number}.billingInfo.type`
      | `productInstances.${number}.billingInfo.cycleDuration.unit`
      | `productInstances.${number}.billingInfo.cycleDuration.count`
      | `productInstances.${number}.instanceId`
      | `productInstances.${number}.catalogProductId`
      | `productInstances.${number}.status`
      | `productInstances.${number}.failure.code`
      | `productInstances.${number}.failure.message`,
      6
    >
  >;
}

export function queryPackages(httpClient: HttpClient): QueryPackagesSignature {
  return () =>
    universalQueryPackages(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryPackagesSignature {
  /**
   * Retrieves a list of packages, given the provided paging, filtering, and sorting.
   *
   *
   * You must pass the ID of the Wix account that the packages belong to in the
   * header of the call. The call returns packages that belong to the parent
   * account, regardless if you pass the ID of a parent or sub-account. In case
   * you want to retrieve only packages that belong to a sub-account, you must
   * pass the sub-account ID as `filter` in the `query` object like this:
   *
   * ```json
   * {
   * "query": {
   * "filter": {
   * "accountId": {
   * "$eq": "<SUB_ACCOUNT_ID>"
   * }
   * }
   * }
   * }
   * ```
   *
   * Query Packages runs with these defaults, which you can override:
   *
   * - `createdDate` is sorted in `DESC` order
   * - `cursorPaging.limit` is `100`
   *
   * By default `pagingMetadata.cursors` are returned, unless you specifically pass
   * `query.paging`.
   *
   * The maximum for `cursorPaging.limit` is `100`.
   *
   * If you pass a cursor token that you have received in a previous Query Package response,
   * passing additional sort or filter information results in an invalid cursor
   * error. Since the received cursor token already holds all the needed information
   * for sort and filter. Changing the sort and filter properties during the span of
   * a cursor query isn't supported. But you can provide a different limit.
   *
   * To learn about working with _Query_ endpoints, see
   * [API Query Language](https://dev.wix.com/api/rest/getting-started/api-query-language),
   * [Sorting and Paging](https://dev.wix.com/api/rest/getting-started/pagination),
   * and [Field Projection](https://dev.wix.com/api/rest/getting-started/field-projection).
   *
   * > **Important**: This call requires an account level API key and cannot be
   * > authenticated with the standard authorization header.
   */
  (): PackagesQueryBuilder;
}

export function typedQueryPackages(
  httpClient: HttpClient
): TypedQueryPackagesSignature {
  return (query: PackageQuery) =>
    universalTypedQueryPackages(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryPackagesSignature {
  /** */
  (query: PackageQuery): Promise<
    NonNullablePaths<
      QueryPackagesResponse,
      `packages` | `packages.${number}._id` | `packages.${number}.accountId`,
      4
    >
  >;
}

export function assignProductInstanceToSite(
  httpClient: HttpClient
): AssignProductInstanceToSiteSignature {
  return (
    identifiers: NonNullablePaths<
      AssignProductInstanceToSiteIdentifiers,
      `instanceId` | `siteId`,
      2
    >,
    options?: AssignProductInstanceToSiteOptions
  ) =>
    universalAssignProductInstanceToSite(
      identifiers,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface AssignProductInstanceToSiteSignature {
  /**
   * Assigns a product instance to a site.
   *
   *  You must pass the ID of the Wix account that the product instance belongs to in the header of the call.
   *
   *  The customer immediately gains access to features included in the assigned service. It's up to the reseller to decide whether assigning a product instance affects the customer’s payment.
   *
   *  It's possible to assign a product instance that's already assigned to a site, to a different site without [unassigning](/packages/unassign-product-instance-from-site) it first. But if the call fails in such a situation, the product instance may either remain assigned to the original site or become unassigned. You can confirm the instance's status by [retrieving the relevant package](/packages/get-package).
   *
   * > **Important:** This call requires an account level API key and cannot be authenticated with the standard authorization header.
   */
  (
    identifiers: NonNullablePaths<
      AssignProductInstanceToSiteIdentifiers,
      `instanceId` | `siteId`,
      2
    >,
    options?: AssignProductInstanceToSiteOptions
  ): Promise<
    NonNullablePaths<
      AssignProductInstanceToSiteResponse,
      | `package._id`
      | `package.accountId`
      | `package.productInstances`
      | `package.productInstances.${number}.billingInfo.type`
      | `package.productInstances.${number}.billingInfo.cycleDuration.unit`
      | `package.productInstances.${number}.billingInfo.cycleDuration.count`
      | `package.productInstances.${number}.instanceId`
      | `package.productInstances.${number}.catalogProductId`
      | `package.productInstances.${number}.status`
      | `package.productInstances.${number}.failure.code`
      | `package.productInstances.${number}.failure.message`,
      7
    >
  >;
}

export function unassignProductInstanceFromSite(
  httpClient: HttpClient
): UnassignProductInstanceFromSiteSignature {
  return (
    instanceId: string,
    options?: UnassignProductInstanceFromSiteOptions
  ) =>
    universalUnassignProductInstanceFromSite(
      instanceId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UnassignProductInstanceFromSiteSignature {
  /**
   * Unassigns a product instance from a site.
   *
   *  You must pass the ID of the Wix account that the product instance belongs to in the header of the call.
   *
   *  The product instance becomes floating and the customer immediately looses access to features included in the unassigned service. It's up to the reseller to decide whether unassigning a product instance affects the customer’s payment.
   *
   *  If an unassigned product instance is a requirement for another service, that service may not be available to the customer any longer. For example, if you unassign a `Combo Plan` plan from a site, a previously connected domain is disconnected.
   *
   *  You can assign floating product instances to sites using the [Assign Product Instance to Site](/packages/assign-product-instance-to-site) method.
   *
   * > **Important:** This call requires an account level API key and cannot be authenticated with the standard authorization header.
   * @param - ID of the product instance to unassign from a site.
   */
  (
    instanceId: string,
    options?: UnassignProductInstanceFromSiteOptions
  ): Promise<
    NonNullablePaths<
      UnassignProductInstanceFromSiteResponse,
      | `package._id`
      | `package.accountId`
      | `package.productInstances`
      | `package.productInstances.${number}.billingInfo.type`
      | `package.productInstances.${number}.billingInfo.cycleDuration.unit`
      | `package.productInstances.${number}.billingInfo.cycleDuration.count`
      | `package.productInstances.${number}.instanceId`
      | `package.productInstances.${number}.catalogProductId`
      | `package.productInstances.${number}.status`
      | `package.productInstances.${number}.failure.code`
      | `package.productInstances.${number}.failure.message`,
      7
    >
  >;
}

export function cancelProductInstance(
  httpClient: HttpClient
): CancelProductInstanceSignature {
  return (instanceId: string, options?: CancelProductInstanceOptions) =>
    universalCancelProductInstance(
      instanceId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CancelProductInstanceSignature {
  /**
   * Cancels a product instance.
   *
   *
   * You must pass the ID of the Wix account that the product instance belongs
   * to in the header of the call. The call fails, if the product instance and
   * Wix account don't match.
   *
   * The customer immediately loses access to features included in the
   * canceled service. It's up to the reseller to decide whether the adjustment
   * affects the customer’s payment.
   *
   * If a canceled service is a requirement for another service, that service
   * may not be available to the customer any longer. For example, if you cancel
   * a `Combo Plan` plan, a previously connected domain is disconnected
   * from the site.
   *
   *
   * > **Important**: This call requires an account level API key and cannot be
   * > authenticated with the standard authorization header.
   * @param - ID of the product instance to cancel.
   */
  (instanceId: string, options?: CancelProductInstanceOptions): Promise<
    NonNullablePaths<
      CancelProductInstanceResponse,
      | `package._id`
      | `package.accountId`
      | `package.productInstances`
      | `package.productInstances.${number}.billingInfo.type`
      | `package.productInstances.${number}.billingInfo.cycleDuration.unit`
      | `package.productInstances.${number}.billingInfo.cycleDuration.count`
      | `package.productInstances.${number}.instanceId`
      | `package.productInstances.${number}.catalogProductId`
      | `package.productInstances.${number}.status`
      | `package.productInstances.${number}.failure.code`
      | `package.productInstances.${number}.failure.message`,
      7
    >
  >;
}

export function adjustProductInstanceSpecifications(
  httpClient: HttpClient
): AdjustProductInstanceSpecificationsSignature {
  return (
    instanceId: string,
    options?: AdjustProductInstanceSpecificationsOptions
  ) =>
    universalAdjustProductInstanceSpecifications(
      instanceId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface AdjustProductInstanceSpecificationsSignature {
  /**
   * Upgrades or downgrades a product instance. For example, you can upgrade a
   * customer's `Business Unlimited Premium` plan to `Business VIP` using this
   * endpoint.
   *
   *
   * You must pass the ID of the Wix account that the product instance belongs
   * to in the header of the call. The call fails, if the product instance and
   * Wix account don't match.
   *
   * The customer has immediate access to new features, while losing access to
   * features not included in the new service. It's up to the reseller to
   * decide whether the adjustment affects the customer’s payment.
   *
   * You can only exchange a product instance with a service of the same type.
   * Contact the [Wix B2B sales team](mailto:bizdev@wix.com) for details about
   * which services belong to the same type.
   *
   * You must provide either a new `catalogProductId` or a new `billingInfo`
   * object. The new billing cycle must be supported for the product. Contact the
   * [Wix B2B sales team](mailto:bizdev@wix.com) for information
   * about which billing cycles are supported for each product. If you adjust the
   * billing cycle for a product instance with `RECURRING` payments, you must
   * also provide `billingInfo.cycleDuration.unit`.
   *
   * If a removed feature is a requirement for another service, that service
   * may not be available to the customer any longer. For example, if you downgrade a
   * `Business Unlimited` plan to `Business Basic`, the site owners won’t be
   * able to use `Pro eCommerce Features` any longer.
   *
   * > **Important**: This call requires an account level API key and cannot be
   * > authenticated with the standard authorization header.
   * @param - ID of the product instance to adjust.
   */
  (
    instanceId: string,
    options?: AdjustProductInstanceSpecificationsOptions
  ): Promise<
    NonNullablePaths<
      AdjustProductInstanceSpecificationsResponse,
      | `package._id`
      | `package.accountId`
      | `package.productInstances`
      | `package.productInstances.${number}.billingInfo.type`
      | `package.productInstances.${number}.billingInfo.cycleDuration.unit`
      | `package.productInstances.${number}.billingInfo.cycleDuration.count`
      | `package.productInstances.${number}.instanceId`
      | `package.productInstances.${number}.catalogProductId`
      | `package.productInstances.${number}.status`
      | `package.productInstances.${number}.failure.code`
      | `package.productInstances.${number}.failure.message`,
      7
    >
  >;
}

export function updatePackageExternalId(
  httpClient: HttpClient
): UpdatePackageExternalIdSignature {
  return (
    identifiers: NonNullablePaths<
      UpdatePackageExternalIdIdentifiers,
      `externalId` | `packageId`,
      2
    >
  ) =>
    universalUpdatePackageExternalId(
      identifiers,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdatePackageExternalIdSignature {
  /**
   * Updates a package's external ID field.
   */
  (
    identifiers: NonNullablePaths<
      UpdatePackageExternalIdIdentifiers,
      `externalId` | `packageId`,
      2
    >
  ): Promise<
    NonNullablePaths<
      UpdatePackageExternalIdResponse,
      | `updatedPackage._id`
      | `updatedPackage.accountId`
      | `updatedPackage.productInstances`
      | `updatedPackage.productInstances.${number}.billingInfo.type`
      | `updatedPackage.productInstances.${number}.billingInfo.cycleDuration.unit`
      | `updatedPackage.productInstances.${number}.billingInfo.cycleDuration.count`
      | `updatedPackage.productInstances.${number}.instanceId`
      | `updatedPackage.productInstances.${number}.catalogProductId`
      | `updatedPackage.productInstances.${number}.status`
      | `updatedPackage.productInstances.${number}.failure.code`
      | `updatedPackage.productInstances.${number}.failure.message`,
      7
    >
  >;
}

export {
  AccountInfo,
  ActionEvent,
  AdjustProductInstanceSpecificationsOptions,
  AdjustProductInstanceSpecificationsRequest,
  AdjustProductInstanceSpecificationsResponse,
  AdjustedProductInstanceSpecifications,
  AssignProductInstanceToSiteIdentifiers,
  AssignProductInstanceToSiteOptions,
  AssignProductInstanceToSiteRequest,
  AssignProductInstanceToSiteResponse,
  AssignedProductInstanceToSite,
  BillingReference,
  Cancel,
  CancelPackageOptions,
  CancelPackageRequest,
  CancelPackageResponse,
  CancelProductInstanceOptions,
  CancelProductInstanceRequest,
  CancelProductInstanceResponse,
  CancellationDetails,
  Complete,
  ContractSwitchReason,
  ContractSwitchType,
  ContractSwitched,
  CountPackagesRequest,
  CountPackagesResponse,
  CreatePackageOptions,
  CreatePackageRequest,
  CreatePackageResponse,
  CreatePackageV2Options,
  CursorPaging,
  Cursors,
  Cycle,
  CycleCycleSelectorOneOf,
  CycleDescriptorType,
  CycleInterval,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  FailureReason,
  FailureReasonCode,
  FailureReportRequest,
  FieldToUpdate,
  FixBrokenMigrationRequest,
  FixBrokenMigrationResponse,
  GetPackageRequest,
  GetPackageResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  Initiator,
  Interval,
  IntervalIntervalUnit,
  IntervalUnit,
  MessageEnvelope,
  MigrateSubscriptionToPackagesRequest,
  MigrateSubscriptionToPackagesResponse,
  OneTime,
  Package,
  PackageCanceled,
  PackageQuerySpec,
  PackagesQueryBuilder,
  PackagesQueryResult,
  Paging,
  PagingMetadataV2,
  PriceIncreaseTrigger,
  ProductAdjustment,
  ProductInstance,
  ProductInstanceCanceled,
  ProductInstanceContractDetailsOneOf,
  ProductInstanceCycle,
  ProductInstanceFailed,
  ProductInstanceUpdated,
  ProductPriceIncreaseData,
  ProviderName,
  QueryPackagesRequest,
  QueryPackagesResponse,
  QueryV2,
  QueryV2PagingMethodOneOf,
  ReactivationData,
  ReactivationReasonEnum,
  RecurringChargeAttemptFailed,
  RecurringChargeSucceeded,
  RefundType,
  Reschedule,
  RestoreInfo,
  SortOrder,
  Sorting,
  Status,
  Subscription,
  SubscriptionAssigned,
  SubscriptionAutoRenewTurnedOff,
  SubscriptionAutoRenewTurnedOn,
  SubscriptionCancelled,
  SubscriptionCreated,
  SubscriptionEvent,
  SubscriptionEventEventOneOf,
  SubscriptionNearEndOfPeriod,
  SubscriptionPendingChange,
  SubscriptionStatus,
  SubscriptionTransferred,
  SubscriptionUnassigned,
  Task,
  TaskAction,
  TaskActionActionOneOf,
  TaskKey,
  UnassignProductInstanceFromSiteOptions,
  UnassignProductInstanceFromSiteRequest,
  UnassignProductInstanceFromSiteResponse,
  UnassignReason,
  UnassignedProductInstanceFromSite,
  UpdatePackageExternalIdIdentifiers,
  UpdatePackageExternalIdRequest,
  UpdatePackageExternalIdResponse,
  UpdateProductInstanceRequest,
  UpdateProductInstanceResponse,
  WebhookIdentityType,
  utils,
} from './premium-reseller-v1-packages-packages.universal.js';
